import React from 'react';
import 'react-dropzone-uploader/dist/styles.css';
import { useDispatch, useSelector } from 'react-redux';
import * as assetsActions from '../../../redux/actions/assetsAction';
import { Box, Divider, Grid, IconButton, LinearProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RemoveCircleRoundedIcon from '@material-ui/icons/RemoveCircleRounded';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import DisabledIcon from '@material-ui/icons/NotInterested';
import Dropzone from 'react-dropzone-uploader';
import { SnackbarProvider } from 'notistack';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import AssetTextFieldContainer from '../AssetNameTextField';
import ShallowCopyFileObject from '../../../Utils/FileUtils';
import CancelIcon from '@material-ui/icons/Cancel';
import FileIcon from '../../../icons/FileIcon';
import { formatBytes } from '../../../Utils/GlobalUtils';
import PrimaryModelBtn from '../../Files/PrimaryModelBtn';
import { openErrorSnackBar } from '../../../redux/actions/snackBarActions';
import { fileNameLenWarningMsg, sourceFileNameLenLimit } from '../../../Constants/Files';

// const assetNameLbl = "3D ASSET";
const emptyAssetMsg = 'There are currently no assets. To add assets use the Upload CSV button or Add Assets button.';
const dropFilesHereTxt = 'Drop files here or click to browse for file(s) to upload.';
const enterValidAssetName = 'Enter a unique asset name to enable file upload.';

const useStyles = makeStyles((theme) => ({
	disabled: {
		fill: theme.palette.neutral.n400,
		color: theme.palette.neutral.n400,
	},
	divider: { margin: '1rem 0 0 0' },
	dropfilesContainer: {
		marginTop: '1.5rem',
		width: '100%',
	},
	dropZoneRejectStatus: {
		borderColor: theme.palette.alerts.error,
		backgroundColor: '#DAA',
	},
	dropzone: {
		'& .dzu-dropzone': {
			backgroundColor: 'white',
			borderStyle: 'solid',
			overflow: 'auto',
			borderColor: theme.palette.primary.main,
			display: 'flex',
			justifyContent: 'center',
		},
		'& .dzu-dropzoneDisabled': {
			borderColor: theme.palette.neutral.n400,
			borderStyle: 'dashed',
		},
		'& .dzu-inputLabelWithFiles': {
			alignSelf: 'center',
			backgroundColor: 'unset',
			marginTop: 'unset',
			marginLeft: 'unset',
		},
		'& .dzu-previewContainer': {
			padding: '1.25rem',
			'& .dzu-previewImage': {
				maxHeight: '4rem',
				maxWidth: '4rem',
			},
		},
		'& .dzu-previewStatusContainer': {
			padding: '0 0 0 2rem',
			flexGrow: 1,
			'& progress': { flexGrow: 1 },
		},
	},
	enabled: {
		fill: theme.palette.primary.main,
		color: theme.palette.primary.main,
	},
	toggleElementVisibility: { display: 'none' },
	file: {
		height: '7rem',
		width: '100%',
	},
	progress: {
		flexGrow: 1,
		paddingRight: '0.25rem',
	},
	cancel: {
		color: '#eb3b5a',
		height: '1.25rem',
		width: '1.25rem',
	},
}));

//files drop zone container logo and text rendering
const DropfilesContainer = (classes, index, extra, isAssetNameValid) => {
	return (
		<Grid container item direction="column" alignItems="center" justify="center" key={index} wrap="nowrap" xs={12}>
			<Grid item>
				{isAssetNameValid ? (
					<CloudUploadIcon className={classes.enabled} fontSize="large" />
				) : (
					<DisabledIcon className={classes.disabled} fontSize="large" />
				)}
			</Grid>
			<Grid item>
				{isAssetNameValid ? (
					<Typography className={classes.enabled}>{dropFilesHereTxt}</Typography>
				) : (
					<Typography className={classes.disabled}>{enterValidAssetName}</Typography>
				)}
			</Grid>
		</Grid>
	);
};

//uploader container
const MyUploader = (props) => {
	const { index, dispatch, classes, asset } = props;
	// Checking if asset name is valid.
	const isAssetNameValid = asset.isAssetNameUnqiueOnUI && asset.isAssetNameUniqueInBackEnd && asset.name.length > 0;
	const [initialFiles, setInitialFiles] = useState([]);

	React.useEffect(() => {
		setInitialFiles(asset.sourceFiles);
		// eslint-disable-next-line
	}, []);

	// specify upload params and url for your files
	const anchorOrigin = {
		vertical: 'bottom',
		horizontal: 'center',
	};

	//wrapping up file objects and post url to send to upload component using FormData with meta data
	//Snack bar notifications enqueueing using notistack.
	const { enqueueSnackbar } = useSnackbar();
	//reusable enqueue config
	const enqueueSnackBarConfig = (message, variant) => {
		return enqueueSnackbar(message, {
			variant,
			anchorOrigin,
		});
	};
	// Custom preview component for file uploader with set PrimaryModelBtn Btn
	const PreviewComponent = (props) => {
		const { meta, fileWithMeta } = props;
		const dispatch = useDispatch();
		// getting isPrimary property for the uploaded file.
		const srcFile = asset.sourceFiles.find((s) => s.name === meta.name);
		// when uploading file for first time srcFile will be "undefined". pass the default isPrimary as false.
		const isPrimary = srcFile?.isPrimary ?? false;
		const fileExtension = meta.name.split('.')[1].toLowerCase();
		const classes = useStyles();
		const handlePrimaryBtnClick = () => {
			const data = {
				isPrimary,
				fileName: meta.name,
				asset,
			};
			dispatch(assetsActions.setModelFileAsPrimary(data));
		};
		const handleRemoveFileBtnClick = () => {
			fileWithMeta.remove();
			// remove file from redux store when it is removed from dropzone.
			const removedFileObj = {
				file: fileWithMeta.file,
				id: index,
				assetName: asset.name,
			};
			dispatch(assetsActions.removeAssetFile(removedFileObj));
		};
		return (
			<Grid className={classes.file} container item alignItems="center" wrap="nowrap">
				<Grid item>
					<FileIcon size={56} type={fileExtension} />
				</Grid>
				<Grid container item direction="column" wrap="nowrap">
					<Grid container alignItems="center">
						<Grid item>
							<Typography className={classes.filename} variant="body1">
								{meta.name}
							</Typography>
						</Grid>
						<Box pl={1} />
						<Grid item>
							<PrimaryModelBtn isPrimary={isPrimary} onClick={handlePrimaryBtnClick} />
						</Grid>
					</Grid>
					<Grid container>
						<Typography variant="body1">Size: {formatBytes(meta.size)}</Typography>
					</Grid>
					<Grid container item alignItems="center">
						<Grid className={classes.progress} item>
							<LinearProgress color="secondary" value={100} variant="determinate" />
						</Grid>
						<Grid item>
							<IconButton size="small">
								<CancelIcon className={classes.cancel} onClick={handleRemoveFileBtnClick} />
							</IconButton>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		);
	};

	// called every time a file's `status` changes
	const handleChangeStatus = (fileWithMeta, status) => {
		//different configs for upload, remove and failed upload status
		const { file } = fileWithMeta;
		const { name } = file;
		if (status === 'done') {
			// throw an error if the source file name length is greater than 100 characters.
			if (name.length > sourceFileNameLenLimit) {
				dispatch(openErrorSnackBar(fileNameLenWarningMsg));
				fileWithMeta.remove();
				return;
			}
			// check to see if the file is already uploaded.
			const isFileAlreadyUploaded = asset.sourceFiles.some(
				(f) => f.name.toLowerCase() === file.name.toLowerCase(),
			);

			// only upload the file, if it is not already uploaded for this asset.
			// when providing initialFiles props to dropzone uploader, when the component is re-rendered again, it triggers file upload for all the files in initialFiles list. which results in duplicate files on dropzone preview.
			if (!isFileAlreadyUploaded) {
				const shallowFileObj = ShallowCopyFileObject(file);
				// making first uploaded file as Primary by default.
				if (asset.sourceFiles.length === 0) {
					shallowFileObj.isPrimary = true;
				}
				const fileObj = {
					sourceFiles: [shallowFileObj],
					id: index,
				};
				dispatch(assetsActions.addAssetFiles(fileObj));
				//Snack bar success message
				enqueueSnackBarConfig(`File ${file.name} Has been uploaded`, 'success');
			}
		}
	};

	//Custom layout to display preview zone below dropzone
	const Layout = ({ input, previews, dropzoneProps, files, extra: { maxFiles } }) => {
		return (
			<Grid container item alignItems="center">
				<Grid item {...dropzoneProps}>
					{files.length < maxFiles && input}
				</Grid>
				<Grid item xs={12}>
					{previews}
				</Grid>
			</Grid>
		);
	};
	return (
		//dropzone props
		<Grid className={classes.dropzone} item>
			<Dropzone
				onChangeStatus={handleChangeStatus}
				inputContent={(files, extra) => {
					return DropfilesContainer(props.classes, index, files, extra, isAssetNameValid);
				}}
				LayoutComponent={Layout}
				inputWithFilesContent={(extra, files) => {
					return DropfilesContainer(props.classes, index, files, extra, isAssetNameValid);
				}}
				accept=".glb"
				initialFiles={initialFiles}
				multiple={true}
				maxFiles={100}
				PreviewComponent={PreviewComponent}
				//disable dropzone when asset name is not valid
				disabled={!isAssetNameValid}
				addClassNames={{
					dropzoneReject: props.classes.dropZoneRejectStatus,
				}}
			/>
		</Grid>
	);
};

//Asset container
export const SingleAssetUploader = React.memo((props) => {
	const { dispatch, classes, asset, lastIndex, index } = props;
	const [openUploader, setUploaderOpenStatus] = useState(true);

	//Asset uploader collapse and expand status
	const toggleSoloUploader = () => {
		setUploaderOpenStatus(!openUploader);
	};

	const deleteSoloAssetUploader = (props) => {
		dispatch(assetsActions.removeAssets(props.index));
	};

	return (
		<Grid container item alignItems="center" direction="column" key={index} data-cy={'asset-container-' + index}>
			<Grid container item wrap="nowrap">
				<Grid item xs={1}>
					{openUploader ? (
						<IconButton onClick={toggleSoloUploader}>
							<RemoveIcon />
						</IconButton>
					) : (
						<IconButton id={index} onClick={toggleSoloUploader}>
							<AddIcon />
						</IconButton>
					)}
				</Grid>
				<Grid item xs={10}>
					<AssetTextFieldContainer {...props} />
				</Grid>
				<Grid item xs={1}>
					<IconButton id={index} onClick={() => deleteSoloAssetUploader(props)}>
						<RemoveCircleRoundedIcon data-cy={'remove-circle-icon-' + index} />
					</IconButton>
				</Grid>
			</Grid>
			<Grid
				item
				className={openUploader ? `${classes.dropfilesContainer}` : `${classes.toggleElementVisibility}`}
			>
				{/* Wrapping Snackbar provider around uploader container */}
				<SnackbarProvider maxSnack={4}>
					<MyUploader key={index} classes={classes} index={index} dispatch={dispatch} asset={asset} />
				</SnackbarProvider>
			</Grid>
			{/* HR element should not be displayed for last element  */}
			{index !== lastIndex ? (
				<Grid container item wrap="nowrap">
					<Grid item xs={12}>
						<Divider className={classes.divider} />
					</Grid>
				</Grid>
			) : null}
		</Grid>
	);
});

const EmtpyAssetMessage = () => {
	return (
		<Grid item>
			<Typography variant="body2" data-cy="assets-empty-msg">
				{emptyAssetMsg}
			</Typography>
		</Grid>
	);
};

const AssetFileUploader = React.memo(() => {
	const classes = useStyles();
	const options = useSelector((state) => state.search.options);
	const assets = useSelector((state) => state.assets);
	const lastIndex = assets[assets.length - 1] ? assets[assets.length - 1].id : 0;
	const dispatch = useDispatch();

	const assetCount = assets.length;
	let assetUploaders = [];
	const assetUploadersProps = {
		dispatch,
		options,
		classes,
		assetCount,
		lastIndex,
		assets,
	};
	// pushing assets to array to render on the UI basing on the count
	for (let index = 0; index < assets.length; index++) {
		const id = assets[index].id;
		assetUploaders.push(
			<SingleAssetUploader {...assetUploadersProps} asset={assets[index]} index={id} key={id} id={id} />,
		);
	}
	return assetCount === 0 ? EmtpyAssetMessage(classes) : assetUploaders;
});

export default AssetFileUploader;
